import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { toast } from '@/components/feedback/useToast';
import { Button } from '@/components/ui/Button';
import { useBiometricUpdateMutation } from '@/features/biometric/hooks/use-biometric-mutations';
import {
  BiometricApiError,
  type BiometricKey,
  type BiometricRange,
} from '@/features/biometric/types/biometric.types';
import {
  BIOMETRIC_DEFAULTS,
  BIOMETRIC_META,
} from '@/features/biometric/utils/biometric.defaults';
import { useWriteDenyGuard } from '@/shared/hooks/useWriteGuard';

interface Props {
  metricKey: BiometricKey;
  title: string;
  serverValues: BiometricRange;
  canWrite: boolean;
}

function makeBiometricSchema(absMin: number, absMax: number, step: number) {
  const isInt = step === 1;
  const base = z
    .number({ error: 'biometricData.errors.required' })
    .positive('biometricData.errors.minValue')
    .min(absMin, 'biometricData.errors.rangeInvalid')
    .max(absMax, 'biometricData.errors.rangeInvalid');
  // Steps & heart rate are integers; sleep allows at most 1 decimal place (PRD 09 §3.2).
  const field = isInt
    ? base.int()
    : base.refine(
        (n) => Math.abs(n * 10 - Math.round(n * 10)) < 1e-9,
        'biometricData.errors.decimalPlaces',
      );
  return z
    .object({
      warnLow: field,
      normalMin: field,
      normalMax: field,
      warnHigh: field,
    })
    .superRefine((v, ctx) => {
      const allInRange = [
        v.warnLow,
        v.normalMin,
        v.normalMax,
        v.warnHigh,
      ].every((n) => Number.isFinite(n) && n >= absMin && n <= absMax);
      if (!allInRange) return;
      if (
        !(
          v.warnLow < v.normalMin &&
          v.normalMin < v.normalMax &&
          v.normalMax < v.warnHigh
        )
      ) {
        for (const path of ['warnLow', 'normalMin', 'normalMax', 'warnHigh']) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'biometricData.errors.orderInvalid',
            path: [path],
          });
        }
      }
    });
}

type FormValues = BiometricRange;

export function BiometricCard({
  metricKey,
  title,
  serverValues,
  canWrite,
}: Props) {
  const { t } = useTranslation('admin');
  // SHCT-582 — PRD 21 §7.4 P-2: the footer used to disappear without METADATA:WRITE, so a
  // READ-only operator saw a form with no way to save and no statement of why. It now stays,
  // `disabled` + 툴팁, and the threshold inputs (already disabled) gain the same wording.
  const { denyProps } = useWriteDenyGuard(canWrite);
  const { step, absMin, absMax } = BIOMETRIC_META[metricKey];
  const defaults = BIOMETRIC_DEFAULTS[metricKey];
  const schema = makeBiometricSchema(absMin, absMax, step);
  const mutation = useBiometricUpdateMutation();

  const {
    register,
    handleSubmit,
    reset,
    trigger,
    formState: { errors, isDirty },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: serverValues,
    mode: 'onBlur',
    reValidateMode: 'onChange',
  });

  // Sync server data into form when it changes (e.g. after invalidateQueries)
  useEffect(() => {
    reset(serverValues);
  }, [serverValues, reset]);

  const FORM_FIELDS = [
    'warnLow',
    'normalMin',
    'normalMax',
    'warnHigh',
  ] as const;
  // SHCT-362/681 — PRD 09 §8.1 gives a failure one of two homes, by what it is about:
  //   해당 필드 하단   빈 값 · 0 이하 값 · 숫자 아닌 값        → under the input that is wrong
  //   해당 카드 에러 영역  임계값 순서 위반 · 절대범위 초과       → the card's error area
  // Everything used to go to the card area, and only the FIRST message was printed, so a second
  // broken rule left its input outlined red with its reason nowhere on screen. (절대범위 초과 is a
  // single-field check but PRD puts it in the card area explicitly, so that is where it goes.)
  const CARD_LEVEL_ERRORS: ReadonlySet<string> = new Set([
    'biometricData.errors.orderInvalid',
    'biometricData.errors.rangeInvalid',
  ]);

  const inlineError = (field: (typeof FORM_FIELDS)[number]) => {
    const key = errors[field]?.message;
    return typeof key === 'string' && !CARD_LEVEL_ERRORS.has(key)
      ? t(key, { min: absMin, max: absMax })
      : null;
  };

  // Deduped: one rule raises on several inputs at once, so the same message would otherwise repeat.
  const cardErrors = [
    ...new Set(
      FORM_FIELDS.map((f) => errors[f]?.message).filter(
        (key): key is string =>
          typeof key === 'string' && CARD_LEVEL_ERRORS.has(key),
      ),
    ),
  ].map((key) => t(key, { min: absMin, max: absMax }));

  const hasError = Object.keys(errors).length > 0;

  function onReset() {
    // Keep server values as the dirty baseline so resetting to defaults marks
    // the form dirty (Save enabled) when defaults differ from the saved values,
    // and stays clean (Save disabled) when they already match. PRD 09 §3.3.
    reset(defaults, { keepDefaultValues: true });
  }

  // Synchronous in-flight lock: prevents a rapid double-click from firing a
  // second save before mutation.isPending flips and disables the button.
  const savingRef = useRef(false);

  function onSave(values: FormValues) {
    if (savingRef.current) return;
    savingRef.current = true;
    mutation.mutate(
      { key: metricKey, range: values },
      {
        onSuccess: () => {
          toast.success(t('biometricData.toasts.saved'));
          // Make the saved values the clean baseline immediately so isDirty
          // flips to false now, not ~400ms later when the invalidateQueries
          // refetch lands. Otherwise the button re-enables during that refetch
          // window and a second rapid click fires a duplicate save. (SHCT-228)
          reset(values);
        },
        onError: (err) => {
          // Server-side threshold-order conflicts carry an actionable message;
          // every other failure (system / unknown / network) shows the standard
          // save-failure toast. PRD 09 §3.2.
          const message =
            err instanceof BiometricApiError &&
            err.code === 'BIOMETRIC_THRESHOLD_ORDER'
              ? err.message
              : t('biometricData.toasts.saveError');
          toast.error(message, { ttlMs: 5000 });
        },
        onSettled: () => {
          savingRef.current = false;
        },
      },
    );
  }

  const hasOrderError = FORM_FIELDS.some(
    (f) => errors[f]?.message === 'biometricData.errors.orderInvalid',
  );

  // SHCT-811 — the order rule (warnLow < normalMin < normalMax < warnHigh) is a rule about all four
  // values, so it raises its issue on all four fields at once. React Hook Form, though, writes back
  // only the error for the field it is validating, which left the other three holding a stale message:
  // fixing the order by editing a DIFFERENT input cleared that input alone, and Save stayed disabled
  // until the user went back and touched the field they had already made valid.
  //
  // So when an order error is on screen, editing any of the four revalidates all four — the error
  // clears the way it appeared. Gated on `hasOrderError` on purpose: with no order error there is
  // nothing stale to clear, and revalidating every keystroke would surface errors mid-typing, which
  // this form's `mode: 'onBlur'` deliberately avoids.
  const registerThreshold = (field: (typeof FORM_FIELDS)[number]) =>
    register(field, {
      valueAsNumber: true,
      onChange: () => {
        if (hasOrderError) void trigger([...FORM_FIELDS]);
      },
    });

  const inputClass = (field: keyof FormValues) =>
    `bio-card__input${errors[field] || hasOrderError ? ' is-error' : ''}`;

  // type="number" natively accepts scientific notation (3e3) and signs (+/-);
  // restrict to digits (steps, heart rate) or digits + "." (sleep). Covers typing & paste.
  const allowedChars = step === 1 ? /^[0-9]+$/ : /^[0-9.]+$/;
  const handleBeforeInput = (e: React.FormEvent<HTMLInputElement>) => {
    const { data } = e.nativeEvent as InputEvent;
    if (data != null && !allowedChars.test(data)) {
      e.preventDefault();
    }
  };

  return (
    <div className="bio-card">
      {/* Header */}
      <div className="bio-card__header">
        <h2 className="bio-card__title">{title}</h2>
        <span className="bio-card__unit-badge">
          {t(`biometricData.units.${metricKey}`)}
        </span>
      </div>

      <form onSubmit={handleSubmit(onSave)} noValidate>
        {/* 정상범위 */}
        <section className="bio-card__section">
          <div className="bio-card__section-title">
            {t('biometricData.labels.normalRange')}
          </div>
          <div className="bio-card__row">
            <div className="bio-card__input-group">
              <span>{t('biometricData.labels.normalMin')}</span>
              <input
                type="number"
                step={step}
                className={inputClass('normalMin')}
                disabled={!canWrite}
                {...denyProps}
                onBeforeInput={handleBeforeInput}
                {...registerThreshold('normalMin')}
              />
              {/* PRD 09 §8.1 "해당 필드 하단" */}
              {inlineError('normalMin') && (
                <p className="form-field__error">{inlineError('normalMin')}</p>
              )}
            </div>
            <span className="bio-card__connector">
              {t('biometricData.labels.normalRangeConnector')}
            </span>
            <div className="bio-card__input-group">
              <span>{t('biometricData.labels.normalMax')}</span>
              <input
                type="number"
                step={step}
                className={inputClass('normalMax')}
                disabled={!canWrite}
                {...denyProps}
                onBeforeInput={handleBeforeInput}
                {...registerThreshold('normalMax')}
              />
              {/* PRD 09 §8.1 "해당 필드 하단" */}
              {inlineError('normalMax') && (
                <p className="form-field__error">{inlineError('normalMax')}</p>
              )}
            </div>
          </div>
        </section>

        {/* 주의 임계값 */}
        <section className="bio-card__section">
          <div className="bio-card__section-title">
            {t('biometricData.labels.warnThreshold')}{' '}
            <span className="bio-card__section-subtitle">
              {t('biometricData.labels.warnThresholdHint')}
            </span>
          </div>
          <div className="bio-card__row">
            <div className="bio-card__input-group">
              <span>{t('biometricData.labels.warnLow')}</span>
              <input
                type="number"
                step={step}
                className={inputClass('warnLow')}
                disabled={!canWrite}
                {...denyProps}
                onBeforeInput={handleBeforeInput}
                {...registerThreshold('warnLow')}
              />
              {/* PRD 09 §8.1 "해당 필드 하단" */}
              {inlineError('warnLow') && (
                <p className="form-field__error">{inlineError('warnLow')}</p>
              )}
            </div>
            <span className="bio-card__connector">
              {t('biometricData.labels.warnConnector')}
            </span>
            <div className="bio-card__input-group">
              <span>{t('biometricData.labels.warnHigh')}</span>
              <input
                type="number"
                step={step}
                className={inputClass('warnHigh')}
                disabled={!canWrite}
                {...denyProps}
                onBeforeInput={handleBeforeInput}
                {...registerThreshold('warnHigh')}
              />
              {/* PRD 09 §8.1 "해당 필드 하단" */}
              {inlineError('warnHigh') && (
                <p className="form-field__error">{inlineError('warnHigh')}</p>
              )}
            </div>
          </div>
        </section>

        {/* Error */}
        {/* PRD 09 §8.1 "해당 카드 에러 영역" — the multi-field rules, all of them. */}
        <div className="bio-card__error-msg" role="alert">
          {cardErrors.map((message) => (
            <div key={message}>{message}</div>
          ))}
        </div>

        {/* Footer */}
        <div className="bio-card__footer content__actions">
            <Button
              type="button"
              variant="secondary"
              onClick={onReset}
              disabled={!canWrite || mutation.isPending}
              {...denyProps}
            >
              {t('biometricData.actions.reset')}
            </Button>
            <Button
              type="submit"
              variant="primary"
              disabled={!canWrite || hasError || mutation.isPending || !isDirty}
              {...denyProps}
            >
              {mutation.isPending
                ? t('biometricData.actions.saving')
                : t('biometricData.actions.save')}
            </Button>
        </div>
      </form>
    </div>
  );
}
